import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../utils/api';

function HealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/casc/v1/api/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      setData(json);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // No polling by default; this is a direct access diagnostics page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const healthColor = useMemo(() => {
    if (!data) return '#999';
    if (data.healthy) return '#1a8917'; // green
    if (data.status === 'degraded') return '#b26b00'; // amber
    return '#a80000'; // red/fallback
  }, [data]);

  return (
    <div style={{
      maxWidth: 980,
      margin: '24px auto',
      padding: '16px 20px 24px',
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      color: '#161616',
    }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Health</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 999,
            background: '#f5f5f5',
            border: '1px solid #e5e5e5',
            color: healthColor,
            fontWeight: 600,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: healthColor, display: 'inline-block' }} />
            {data?.status || (loading ? 'loading…' : 'unknown')}
          </span>
          <button
            onClick={load}
            disabled={loading}
            style={{
              border: '1px solid #d0d7de',
              background: loading ? '#f6f8fa' : '#fff',
              padding: '6px 12px',
              borderRadius: 6,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <section style={{ marginBottom: 16, fontSize: 14, color: '#444' }}>
        <div>Endpoint: <code>{`${API_BASE_URL || ''}/casc/v1/api/health`}</code></div>
        {lastRefreshed && (
          <div>Last refreshed: {lastRefreshed.toLocaleString()}</div>
        )}
      </section>

      {error && (
        <div style={{
          padding: '12px 14px',
          border: '1px solid #ffd0d0',
          background: '#fff6f6',
          color: '#7a0000',
          borderRadius: 6,
          marginBottom: 16,
          whiteSpace: 'pre-wrap',
        }}>
          Failed to load health: {String(error?.message || error)}
        </div>
      )}

      <section style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
        {/* Summary cards */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Summary</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <KV label="status" value={data?.status} />
            <KV label="healthy" value={String(data?.healthy)} />
            <KV label="server" value={data?.server} />
            <KV label="timestamp" value={data?.timestamp} />
            <KV label="env" value={data?.checks?.env} />
          </div>
        </div>

        {/* Issues */}
        {Array.isArray(data?.issues) && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 18 }}>Issues</h2>
            {data.issues.length === 0 ? (
              <div style={{ color: '#1a8917' }}>No issues detected</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.issues.map((i, idx) => (
                  <li key={idx} style={{ margin: '6px 0' }}>{i}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Checks (env/services/background) */}
        {data?.checks && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 18 }}>Checks</h2>
            <pre style={{ margin: 0, overflow: 'auto', background: '#0b1020', color: '#e6edf3', padding: 12, borderRadius: 6 }}>
{JSON.stringify(data.checks, null, 2)}
            </pre>
          </div>
        )}
      </section>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, alignItems: 'center' }}>
      <div style={{ color: '#6b7280' }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value == null ? '—' : String(value)}</div>
    </div>
  );
}

export default HealthPage;
